import { MediaConstants } from 'src/constants';
import { ValidationError } from 'src/errors/CustomErrors';

/**
 * Loads and returns dimensions of an image
 * @param url - URL of the image to load
 * @param timeoutMs - Optional timeout in milliseconds
 * @returns Promise resolving to image dimensions
 */
export const loadImageDimensions = (
  url: string,
  timeoutMs = MediaConstants.TIMEOUTS.MEDIA_IMAGE_LOAD,
): Promise<{ width: number; height: number }> => {
  const loadPromise = new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    img.onerror = () => reject(new ValidationError('Failed to load image'));
    img.src = url;
  });
  return withTimeout(loadPromise, timeoutMs);
};

/**
 * Loads and returns dimensions of a video
 * @param url - URL of the video to load
 * @param timeoutMs - Optional timeout in milliseconds
 * @returns Promise resolving to video dimensions
 */
export const loadVideoDimensions = (
  url: string,
  timeoutMs = MediaConstants.TIMEOUTS.MEDIA_VIDEO_LOAD,
): Promise<{ width: number; height: number }> => {
  const loadPromise = new Promise<{ width: number; height: number }>((resolve, reject) => {
    const video = document.createElement(MediaConstants.ELEMENTS.VIDEO);
    video.preload = MediaConstants.VIDEO.DEFAULT_PRELOAD;

    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
    video.onerror = () => {
      reject(new ValidationError('Failed to load video'));
    };
    video.src = url;
  });
  return withTimeout(loadPromise, timeoutMs);
};

/**
 * Adds timeout functionality to a Promise.
 *
 * @param promise - Promise to add timeout to
 * @param timeoutMs - Timeout in milliseconds
 * @returns Promise that rejects if timeout is reached
 * @throws ValidationError when timeout occurs
 */
export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  const timeoutPromise = new Promise<T>((_, reject) => {
    setTimeout(() => reject(new ValidationError('Media Asset loading timed out')), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]);
};

/**
 * Returns the duration (in seconds) of a video File.
 * Creates a temporary video element, loads the file via object URL,
 * and reads duration from the loadedmetadata event.
 */
export const getVideoDuration = (file: File): Promise<number> => {
  const loadPromise = new Promise<number>((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ValidationError('Failed to load video metadata'));
    };
    video.src = url;
  });
  return withTimeout(loadPromise, MediaConstants.TIMEOUTS.MEDIA_VIDEO_LOAD);
};
